import {ProductGrid} from '@/_application/modules/products/components/product-grid';
import {PRODUCTS_PER_PAGE} from '@/framework/client/variables';
import {SortInput, useProductsFilterActions} from '@/_application/modules/products/hooks/use-product-filter-actions';
import {Aggregation, ProductInterface, SortFields} from '__generated__/__types__';
import CategoryFilter from '@/_application/modules/categories/components/category-filter/category-filter';
import ProductSort from '@/_application/modules/categories/components/product-sort';
import {useTranslation} from 'next-i18next';
import {useEffect, useRef, useState, useTransition} from 'react';
import {ProductListItemsQuery} from '@/framework/gql/products.graphql';
import {disabledAttributes} from '@/framework/static/filter-attribute-code-value-type';
import {ViewContentTracking} from '@/_application/modules/tracking/components/view-content-tracking';
import {ViewContentCommandInput} from '@kidslabs/mario-client-event-tracking';
import {PageType} from '@/_application/core/types';
import CategoryProductsPagination from '@/_application/modules/categories/components/category-products-pagination';
import {ProductListItemsQueryOptions} from '@/_application/modules/products/request/product-list.request';
import ProductLoader from '@/_application/core/components/ui/loaders/product-loader';
import rangeMap from '@/lib/range-map';
import {isParamsChanged} from '@/_application/modules/products/helpers/component-render';

interface IProps {
    categoryName?: string;
    isSearch?: boolean;
    initialProducts?: ProductListItemsQuery['products'];
    productListItemsQueryOptions?: ProductListItemsQueryOptions;
    isFetchingProduct?: boolean;
}

function CategoryProducts({
    categoryName,
    isSearch = false,
    initialProducts,
    productListItemsQueryOptions = {
        filter: {},
        sort: {},
        search: '',
        currentPage: 1,
        pageSize: PRODUCTS_PER_PAGE,
    } as ProductListItemsQueryOptions,
    isFetchingProduct,
}: Readonly<IProps>) {
    const {t} = useTranslation('search-page');
    const [isPending, startTransition] = useTransition();

    const {
        fetchData,
        variables,
        products,
        isLoading: isFilteringProduct,
        error,
        handleFilterChange,
        handlePaginationChange,
        handleSortChange,
    } = useProductsFilterActions();

    // Use fetched products if available, otherwise use initialProducts from server
    const productList = isParamsChanged(productListItemsQueryOptions, variables) ? products : initialProducts;

    const filteredAggregations = ((initialProducts?.aggregations as Aggregation[]) || []).filter(
        aggregation => !disabledAttributes.includes(aggregation.attribute_code),
    );

    const isLoadingProduct = isFilteringProduct || isFetchingProduct || isPending;
    const titleLoader = isSearch ? `${t('text-searching')}...` : categoryName;

    useEffect(() => {
        //@: At first time, products is empty
        //@: If products is empty and params isn't changed => don't fetch data
        const hasToReloadData = !products?.items?.length && !isParamsChanged(productListItemsQueryOptions, variables);
        if (!hasToReloadData) {
            fetchData();
        }
    }, [JSON.stringify(variables)]);

    const trackingInput: ViewContentCommandInput = isSearch
        ? {}
        : {
              viewType: 'ViewCategory',
              content_name: categoryName,
              content_category: categoryName,
              contents: productList?.items?.map(product => ({
                  id: (product as ProductInterface)?.id as number,
                  quantity: 1,
              })) as any,
          };

    //: Update pagination change logic from call stack to callback queue
    //: Browser updates UI before action paginationChange logic
    const paginationChangeAction = (value: number) => {
        startTransition(() => {
            handlePaginationChange(value);
        });
    };

    //: Browser updates UI before action filterChange logic
    const filterChangeAction = (values: string[] | string, attributeCode: string) => {
        startTransition(() => {
            handleFilterChange(values, attributeCode);
        });
    };

    const sortingChange = (value: SortInput) => {
        startTransition(() => {
            handleSortChange(value);
        });
    };

    return (
        <section className={'product-grid-wrapper'}>
            <CategoryFilter
                aggregations={filteredAggregations}
                onFilterChange={filterChangeAction}
                isLoading={isFilteringProduct}
            />
            {isLoadingProduct && (
                <div className="mt-5 rounded-2xl bg-white p-5">
                    <h1 className="text-2xl font-bold text-primary">{titleLoader}</h1>
                    <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                        {rangeMap(30, i => (
                            <ProductLoader key={i} uniqueKey={`product-${i}`} />
                        ))}
                    </div>
                </div>
            )}

            {!isLoadingProduct && (
                <div className="mt-5 rounded-2xl bg-white p-5">
                    {isSearch ? (
                        <h1 className="text-2xl font-bold text-primary">
                            {`${t('text-found')} ${productList?.total_count ?? 0} ${t('text-result-for')} "${categoryName}"`}
                        </h1>
                    ) : (
                        <div className="flex items-center justify-between">
                            <h1 className="text-2xl font-bold text-primary">{categoryName}</h1>
                            <ProductSort
                                sortFields={productList?.sort_fields as SortFields}
                                onSortChange={sortingChange}
                            />
                        </div>
                    )}

                    {/* Product */}
                    <div className="flex">
                        {productList?.items && productList.items.length > 0 && trackingInput.contents && (
                            <ViewContentTracking page={PageType.CATEGORY} input={trackingInput} />
                        )}
                        <ProductGrid
                            isSearch={isSearch}
                            products={productList}
                            isLoading={isLoadingProduct}
                            error={error}
                            limit={PRODUCTS_PER_PAGE}
                        />
                    </div>
                    {/* Pagination */}
                    {productList?.page_info?.total_pages! > 1 && (
                        <div className="mt-5">
                            <CategoryProductsPagination
                                pageSize={productList?.page_info?.page_size ?? PRODUCTS_PER_PAGE}
                                totalItems={productList?.total_count as number}
                                handlePaginationChange={paginationChangeAction}
                            />
                        </div>
                    )}
                </div>
            )}
        </section>
    );
}

export default CategoryProducts;
